import React from 'react';
import styled from 'styled-components';

export interface ProgressProps {
  value: number;
  max?: number;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'success' | 'warning' | 'error';
  showLabel?: boolean;
  label?: string;
}

const ProgressContainer = styled.div`
  width: 100%;
`;

const ProgressTrack = styled.div<{ $size: string }>`
  width: 100%;
  height: ${props => {
    switch (props.$size) {
      case 'small': return '4px';
      case 'large': return '12px';
      default: return '8px';
    }
  }};
  background-color: var(--gray-200);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
`;

const ProgressBar = styled.div<{ $percentage: number; $variant: string }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background-color: ${props => {
    switch (props.$variant) {
      case 'success': return 'var(--success-500)';
      case 'warning': return 'var(--warning-500)';
      case 'error': return 'var(--error-500)';
      default: return 'var(--primary-500)';
    }
  }};
  transition: width 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0.1) 75%,
      transparent 75%,
      transparent
    );
    background-size: 1rem 1rem;
    animation: progress-stripes 1s linear infinite;
  }
  
  @keyframes progress-stripes {
    0% {
      background-position: 1rem 0;
    }
    100% {
      background-position: 0 0;
    }
  }
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
`;

export const Progress: React.FC<ProgressProps> = ({
  value,
  max = 100,
  size = 'medium',
  variant = 'primary',
  showLabel = false,
  label,
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  
  return (
    <ProgressContainer>
      <ProgressTrack 
        $size={size}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label || `Progress: ${percentage.toFixed(1)}%`}
      >
        <ProgressBar $percentage={percentage} $variant={variant} />
      </ProgressTrack>
      {showLabel && (
        <ProgressLabel>
          {label && <span>{label}</span>}
          <span>{percentage.toFixed(1)}%</span>
        </ProgressLabel>
      )}
    </ProgressContainer>
  );
};